import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import EmployeeAvatar from "../EmployeeAvatar";
import { getPresignedUrl } from "../../core/data/redux/awsBucketSlice";
import {
  ChatMessage,
  clearError,
  clearMessages,
  clearUploadError,
  fetchMessages,
  fetchUnreadCount,
  fetchChatAttachments,
  sendMessage,
  uploadChatFiles,
} from "../../core/data/redux/nocOutsideJobChatSlice";
import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE_MB,
  validateFileSize,
  validateFileType,
} from "../../utils/fileUtils";
import {
  openChatAttachment,
  openChatAttachmentInExternalView,
  downloadChatAttachment,
  prepareUploadedAttachments,
  getValidNocType,
} from "../../utils/nocChatUtils";

interface NOCChatComponentProps {
  nocRequestId: number;
  nocType: string; // e.g., "Foreign Visit", "Online Course", etc.
  employeeName?: string;
  employeeId?: string;
  employeeImage?: string;
  approverId?: string;
  isApprover?: boolean;
}

const NOCChatComponent: React.FC<NOCChatComponentProps> = ({
  nocRequestId,
  nocType,
  employeeName = "Employee",
  employeeId,
  employeeImage,
  approverId,
  isApprover: isApproverProp,
}) => {
  const dispatch = useDispatch<any>();

  // Chat state from Redux
  const {
    messages = [],
    attachments = [],
    loading: chatLoading,
    unreadCount,
    error: chatError,
    uploading,
    uploadError,
  } = useSelector((state: any) => state.nocChat);

  // Get current user from Redux or localStorage
  const currentUser = useSelector((state: any) => state.auth?.user || state.user);
  const user = localStorage.getItem("user");
  const userJson = user && user !== "undefined" ? JSON.parse(user) : {};
  const currentUserType =
    userJson?.role ||
    userJson?.user_type ||
    currentUser?.role ||
    currentUser?.user_type ||
    "";
  const currentUserId =
    userJson?.empId ||
    userJson?.empID ||
    currentUser?.empId ||
    currentUser?.empID ||
    "";

  const isCurrentUserApprover =
    Boolean(isApproverProp) ||
    (Boolean(approverId) &&
      Boolean(currentUserId) &&
      String(approverId).replace(/^0+/, "") ===
      String(currentUserId).replace(/^0+/, "")) ||
    currentUserType?.toUpperCase() === "APPROVER" ||
    currentUserType?.toUpperCase() === "ADMIN" ||
    currentUserType?.toUpperCase() === "HR";

  const isEmployee =
    currentUserType?.toUpperCase() === "EMPLOYEE" ||
    (!isCurrentUserApprover &&
      Boolean(employeeId) &&
      String(employeeId).replace(/^0+/, "") ===
      String(currentUserId).replace(/^0+/, ""));

  const hasApproverMessage =
    messages &&
    messages.length > 0 &&
    messages.some(
      (m: any) =>
        (approverId &&
          String(m.senderEmpId || "").replace(/^0+/, "") ===
          String(approverId).replace(/^0+/, "")) ||
        m.senderType === "approver" ||
        m.senderType === "admin"
    );

  const hasAnyMessage = Boolean(messages && messages.length > 0);

  const canChat =
    isCurrentUserApprover ||
    (!isEmployee ? true : (hasApproverMessage || hasAnyMessage));

  // Helper function to compare empIds (handles different formats)
  const isMessageFromCurrentUser = (messageSenderId: string): boolean => {
    const userEmpId = currentUserId;

    if (!userEmpId || !messageSenderId) {
      console.log('🔍 EmpId comparison - Missing data:', { userEmpId, messageSenderId });
      return false;
    }

    // Normalize both IDs (remove leading zeros, convert to string, trim)
    const normalizedUserId = String(userEmpId).trim().replace(/^0+/, '') || '0';
    const normalizedMessageId = String(messageSenderId).trim().replace(/^0+/, '') || '0';

    const isMatch = normalizedUserId === normalizedMessageId;

    return isMatch;
  };

  // Local state
  const [showChat, setShowChat] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Set<number>>(new Set());
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [refreshInterval, setRefreshInterval] = useState<NodeJS.Timeout | null>(
    null
  );
  const [previewMedia, setPreviewMedia] = useState<{
    url: string;
    title: string;
    isImage: boolean;
    isPdf: boolean;
  } | null>(null);

  // 🎯 NEW: Document type modal state
  const [showDocTypeModal, setShowDocTypeModal] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<string | null>(null);
  const [pendingMessage, setPendingMessage] = useState<string>("");

  // 🎯 NEW: Document keywords for detection
  const DOCUMENT_KEYWORDS = [
    'document', 'file', 'upload', 'attach', 'attachment',
    'certificate', 'proof', 'scan', 'pdf', 'paper', 'submit',
    'send', 'provide', 'share'
  ];

  // 🎯 NEW: Check if message contains document keywords
  const containsDocumentKeyword = (message: string): boolean => {
    const lowerMessage = message.toLowerCase();
    return DOCUMENT_KEYWORDS.some(keyword =>
      lowerMessage.includes(keyword)
    );
  };

  // 🎯 NEW: Get latest document request from approver
  const latestDocRequest = React.useMemo(() => {
    return messages
      .filter((msg: ChatMessage) =>
        (msg.senderType === 'approver' || msg.senderType === 'admin') &&
        msg.documentType
      )
      .sort((a: ChatMessage, b: ChatMessage) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )[0];
  }, [messages]);

  // Auto-refresh messages every 30 seconds when chat is open
  useEffect(() => {
    if (showChat && nocRequestId) {
      dispatch(fetchMessages({ nocRequestId }));
      dispatch(fetchChatAttachments({ nocRequestId, nocType }));

      const interval = setInterval(() => {
        dispatch(fetchMessages({ nocRequestId }));
      }, 30000); // 30 seconds

      setRefreshInterval(interval);

      return () => {
        if (interval) clearInterval(interval);
      };
    }
  }, [showChat, nocRequestId, nocType, dispatch]);

  // Fetch messages, unread count and attachments on component mount
  useEffect(() => {
    if (nocRequestId) {
      dispatch(fetchMessages({ nocRequestId }));
      dispatch(fetchUnreadCount(nocRequestId));
      dispatch(fetchChatAttachments({ nocRequestId, nocType }));
    }
  }, [nocRequestId, nocType, dispatch]);

  // 🎯 UPDATED: Handle send message with document type detection
  const handleSendMessage = async () => {
    if (!nocRequestId) return;

    const trimmedMsg = newMessage.trim();

    // Condition matching mobile app: if attachment is selected, message text is required
    if (selectedFiles.length > 0 && !trimmedMsg) {
      toast.warning("Please type a message with your attachment before sending.");
      return;
    }

    if (!trimmedMsg && selectedFiles.length === 0) {
      return;
    }

    // Check if message contains document keywords AND user is approver
    if (isCurrentUserApprover && containsDocumentKeyword(trimmedMsg)) {
      setPendingMessage(trimmedMsg);
      setShowDocTypeModal(true);
      return; // Don't send yet, wait for modal
    }

    // Normal message send
    await sendMessageWithDocType(trimmedMsg, null);
  };

  // 🎯 NEW: Send message with document type
  const sendMessageWithDocType = async (
    message: string,
    docType: string | null
  ) => {
    try {
      let uploadedDocs: any[] = [];
      if (selectedFiles.length > 0) {
        const uploadResult = await dispatch(uploadChatFiles(selectedFiles));
        if (uploadChatFiles.fulfilled.match(uploadResult) && uploadResult.payload) {
          uploadedDocs = prepareUploadedAttachments(
            uploadResult.payload,
            selectedFiles,
            docType || latestDocRequest?.documentType
          );
        } else {
          toast.error("Failed to upload attached files. Please try again.");
          return;
        }
      }

      const attNames = uploadedDocs.map((d: any) => d.fileName).join(", ");
      const finalMessage = uploadedDocs.length > 0
        ? (message.trim() ? `${message.trim()}\n📎 ${attNames}` : `📎 ${attNames}`)
        : message.trim();

      await dispatch(
        sendMessage({
          nocRequestId,
          message: finalMessage,
          attachments: uploadedDocs,
          documents: uploadedDocs,
          documentType: docType || (uploadedDocs.length > 0 ? latestDocRequest?.documentType : undefined),
        })
      );

      setNewMessage("");
      const wasWithAttachments = selectedFiles.length > 0;
      setSelectedFiles([]);
      dispatch(fetchMessages({ nocRequestId }));
      if (wasWithAttachments) {
        dispatch(fetchChatAttachments({ nocRequestId, nocType }));
      }
      toast.success(wasWithAttachments ? "Message and attachment sent successfully!" : "Message sent successfully!");
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    }
  };

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files) as File[];
      const validFiles: File[] = [];
      const errors: string[] = [];

      fileArray.forEach((file) => {
        if (!validateFileType(file, ALLOWED_FILE_TYPES)) {
          errors.push(
            `${file.name}: File type not allowed. Allowed types: ${ALLOWED_FILE_TYPES.join(", ")}`
          );
          return;
        }

        if (!validateFileSize(file, MAX_FILE_SIZE_MB)) {
          errors.push(
            `${file.name}: File size too large. Maximum size: ${MAX_FILE_SIZE_MB}MB`
          );
          return;
        }

        validFiles.push(file);
      });

      if (errors.length > 0) {
        toast.error("File validation errors:\n" + errors.join("\n"));
      }

      if (validFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...validFiles]);
      }
    }

    event.target.value = "";
  };

  // 🎯 UPDATED: Confirm file upload with document type
  const confirmFileUpload = async (index: number) => {
    const file = selectedFiles[index];
    if (!file || !nocRequestId) return;

    const trimmedMsg = newMessage.trim();
    if (!trimmedMsg) {
      toast.warning("Please type a message with your attachment before sending.");
      return;
    }

    try {
      setUploadingFiles((prev) => new Set(prev).add(index));

      const uploadResult = await dispatch(uploadChatFiles([file]));

      if (uploadChatFiles.fulfilled.match(uploadResult)) {
        const uploadData = uploadResult.payload[0];

        // 🎯 Include document type if there's a pending request from approver
        const docType = latestDocRequest?.documentType;
        const uploadedDocs = prepareUploadedAttachments([uploadData], [file], docType);

        await dispatch(
          sendMessage({
            nocRequestId,
            message: trimmedMsg,
            attachments: uploadedDocs,
            documentType: docType || undefined
          })
        );

        setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
        setNewMessage("");
        dispatch(fetchMessages({ nocRequestId }));

        // Show success message
        toast.success("File uploaded successfully!");
      } else {
        console.error("Upload failed:", uploadResult.payload);
        toast.error("Failed to upload file. Please try again.");
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      toast.error("Failed to upload file. Please try again.");
    } finally {
      setUploadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  };

  const cancelFileUpload = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleChatToggle = () => {
    setShowChat(!showChat);
    if (!showChat && nocRequestId) {
      dispatch(fetchMessages({ nocRequestId }));
      dispatch(fetchChatAttachments({ nocRequestId, nocType }));
    } else {
      setSelectedFiles([]);
      setUploadingFiles(new Set());
    }
  };

  const formatMessageTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleAttachmentClick = async (rawAttachment: any) => {
    await openChatAttachmentInExternalView(rawAttachment, nocRequestId, nocType, dispatch, attachments);
  };

  const handleDownloadClick = async (e: React.MouseEvent, rawAttachment: any) => {
    e.stopPropagation();
    await downloadChatAttachment(rawAttachment, nocRequestId, nocType, dispatch, attachments);
  };

  return (
    <>
      {/* Communication Section */}
      <div className="card">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0">Communication</h5>
            {!canChat ? (
              <div className="text-muted small">
                <i className="ti ti-info-circle me-1"></i>
                Chat will be available once the approver initiates communication
              </div>
            ) : (
              <button
                className="btn btn-primary btn-sm position-relative"
                onClick={handleChatToggle}
              >
                <i className="ti ti-message me-1"></i>
                {showChat
                  ? "Close Chat"
                  : messages.length > 0
                    ? "View Chat"
                    : "Start Chat"}
                {unreadCount > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                    {unreadCount}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Enhanced Chat Drawer */}
      {showChat && (
        <>
          {/* Chat Drawer */}
          <div
            className="position-fixed bottom-0 end-0 bg-white shadow-lg chat-drawer m-3 border"
            style={{
              width: "400px",
              height: "600px",
              maxHeight: "85vh",
              zIndex: 1050,
              borderRadius: "4px", // Boxy UI
              display: "flex",
              flexDirection: "column",
              animation: "slideUp 0.3s ease-out",
            }}
          >
            {/* Chat Header */}
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom bg-light flex-shrink-0" style={{ borderTopLeftRadius: "4px", borderTopRightRadius: "4px" }}>
              <div className="d-flex align-items-center">
                <button
                  className="btn btn-link text-dark text-decoration-none p-0 me-3"
                  onClick={handleChatToggle}
                >
                  <i className="ti ti-arrow-left fs-20"></i>
                </button>
                <div>
                  <h5 className="mb-0">Chat - NOC {nocType}</h5>
                  <small className="text-muted">{employeeName}</small>
                </div>
              </div>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => dispatch(fetchMessages({ nocRequestId }))}
                disabled={chatLoading}
                title="Refresh Messages"
              >
                <i className="ti ti-refresh"></i>
              </button>
            </div>

            {/* 🎯 NEW: Document Request Alert for Employees */}
            {!isCurrentUserApprover && latestDocRequest && (
              <div className="px-3 pt-3">
                <div className="alert alert-primary d-flex align-items-start mb-0 shadow-sm">
                  <div className="flex-shrink-0 me-3">
                    <i className="ti ti-file-certificate fs-2 text-primary"></i>
                  </div>
                  <div className="flex-grow-1">
                    <h6 className="alert-heading mb-1">
                      <i className="ti ti-alert-circle me-2"></i>
                      Document Request
                    </h6>
                    <p className="mb-2 small">
                      The approver has requested a{' '}
                      <strong>
                        <span
                          className={`badge ${latestDocRequest.documentType === 'Education'
                              ? 'bg-info'
                              : latestDocRequest.documentType === 'Advertising'
                                ? 'bg-warning text-dark'
                                : 'bg-secondary'
                            }`}
                        >
                          {latestDocRequest.documentType}
                        </span>
                      </strong>
                      {' '}document. Please upload the appropriate file below.
                    </p>
                    <div className="d-flex align-items-center text-muted small">
                      <i className="ti ti-user me-1"></i>
                      <span>Requested by <strong>{latestDocRequest.senderName}</strong></span>
                      <span className="mx-2">•</span>
                      <i className="ti ti-clock me-1"></i>
                      <span>{formatMessageTime(latestDocRequest.createdAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Chat Messages */}
            <div className="chat-messages-container flex-grow-1 overflow-auto p-4" style={{ backgroundColor: "#fcfcfc" }}>
              {(chatError || uploadError) && (
                <div className="alert alert-danger alert-dismissible">
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      dispatch(clearError());
                      dispatch(clearUploadError());
                    }}
                  ></button>
                  {chatError || uploadError}
                </div>
              )}

              {chatLoading && messages.length === 0 ? (
                <div className="text-center py-4">
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="ti ti-message-circle fs-1 mb-2"></i>
                  <p>No messages yet. Start the conversation!</p>

                  {/* Demo message to show avatar functionality */}
                  <div className="mt-4 text-start">
                    <div className="mb-3">
                      <small className="text-muted">
                        Preview: How messages will appear with avatars
                      </small>
                    </div>

                    {/* Demo Admin Message */}
                    <div className="d-flex mb-3">
                      <div className="me-2">
                        <EmployeeAvatar
                          empName="Administrator"
                          empId=""
                          size="sm"
                          showName={false}
                          showEmpId={false}
                          className=""
                        />
                      </div>
                      <div
                        className="position-relative"
                        style={{ maxWidth: "75%", wordBreak: "break-word" }}
                      >
                        <div className="rounded-3 p-3 bg-light text-dark border">
                          <div className="d-flex align-items-center mb-1">
                            <small className="fw-bold">Administrator</small>
                            <small className="ms-2 opacity-75">(admin)</small>
                          </div>
                          <p className="mb-1">
                            Your NOC application has been received and is under
                            review.
                          </p>
                          <div className="text-end">
                            <small className="opacity-75">Demo message</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Demo Employee Message */}
                    <div className="d-flex mb-3 justify-content-end">
                      <div
                        className="position-relative"
                        style={{ maxWidth: "70%" }}
                      >
                        <div className="rounded-3 p-3 bg-primary text-white">
                          <div className="d-flex align-items-center mb-1">
                            <small className="fw-bold">{employeeName}</small>
                            <small className="ms-2 opacity-75">
                              (employee)
                            </small>
                          </div>
                          <p className="mb-1">
                            Thank you. I have uploaded all required documents.
                          </p>
                          <div className="text-end">
                            <small className="opacity-75">Demo message</small>
                          </div>
                        </div>
                      </div>
                      <div className="ms-2">
                        <EmployeeAvatar
                          empName={employeeName}
                          empImage={employeeImage}
                          empId={employeeId}
                          size="sm"
                          showName={false}
                          showEmpId={false}
                          className=""
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                messages.map((msg: ChatMessage) => {
                  // Check if this message is from the current logged-in user
                  const isCurrentUserMessage = isMessageFromCurrentUser(msg.senderEmpId);

                  return (
                    <div
                      key={msg.id}
                      className={`d-flex chat-message mb-4 ${isCurrentUserMessage ? "justify-content-end" : ""
                        }`}
                    >
                      {!isCurrentUserMessage && (
                        <div className="me-2">
                          <EmployeeAvatar
                            empName={msg.senderName || "Administrator"}
                            empImage={msg.senderImage}
                            empId={msg.senderEmpId}
                            size="sm"
                            showName={false}
                            showEmpId={false}
                            className=""
                          />
                        </div>
                      )}

                      <div
                        className="position-relative"
                        style={{ maxWidth: "75%", wordBreak: "break-word" }}
                      >
                        <div
                          className={`rounded-2 p-2 shadow-sm ${isCurrentUserMessage
                              ? "bg-primary text-white"
                              : "bg-white text-dark border"
                            }`}
                        >
                          <div className="d-flex align-items-center mb-0.5">
                            <small className="fw-bold" style={{ fontSize: "11px", lineHeight: "1.2" }}>
                              {msg.senderName ||
                                (msg.senderType === "employee"
                                  ? employeeName
                                  : "Administrator")}
                            </small>
                            <small className="ms-1.5 opacity-75" style={{ fontSize: "10px", lineHeight: "1.2" }}>
                              ({msg.senderType})
                            </small>
                          </div>
                          {(() => {
                            const rawMsg = msg.message || "";
                            const paperclipIdx = rawMsg.indexOf("📎");
                            const hasPaperclip = paperclipIdx !== -1;
                            
                            const displayMsgText = hasPaperclip
                              ? rawMsg.substring(0, paperclipIdx).trim()
                              : rawMsg.trim();

                            let extractedAttNames: string[] = [];
                            if (hasPaperclip) {
                              const attPart = rawMsg.substring(paperclipIdx).replace(/^[📎\s]+/, "").trim();
                              extractedAttNames = attPart.split(",").map((s: string) => s.trim()).filter(Boolean);
                            }

                            const attachmentsList: any[] =
                              msg.attachments && msg.attachments.length > 0
                                ? msg.attachments
                                : extractedAttNames.length > 0
                                  ? extractedAttNames
                                  : [];

                            return (
                              <>
                                {displayMsgText && (
                                  <p
                                    className="mb-1 chat-message-content"
                                    style={{ lineHeight: "1.3", fontSize: "12px" }}
                                  >
                                    {displayMsgText}
                                  </p>
                                )}

                                {/* 🎯 Document Type Badge */}
                                {msg.documentType && (
                                  <div className="mt-1 pt-1 border-top border-opacity-25">
                                    <span
                                      className={`badge d-inline-flex align-items-center ${msg.documentType === 'Education'
                                          ? 'bg-info'
                                          : msg.documentType === 'Advertising'
                                            ? 'bg-warning text-dark'
                                            : 'bg-secondary'
                                        }`}
                                      style={{
                                        fontSize: '9px',
                                        padding: '1.5px 5px',
                                        fontWeight: 600,
                                        lineHeight: '1.2',
                                      }}
                                    >
                                      <i className="ti ti-file-certificate me-1" style={{ fontSize: "10px" }}></i>
                                      Requested: {msg.documentType}
                                    </span>
                                  </div>
                                )}

                                {attachmentsList.length > 0 && (
                                  <div className="mt-1 pt-1 border-top border-opacity-25">
                                    {attachmentsList.map(
                                      (attachment: any, index: number) => {
                                        const rawName =
                                          typeof attachment === "string"
                                            ? attachment
                                            : attachment.fileName ||
                                            attachment.name ||
                                            attachment.FileName ||
                                            "Attachment";
                                        const cleanRaw = rawName.split("/").pop() || rawName;
                                        const parts = cleanRaw.split("-");
                                        const fileName = parts.length >= 3 && /^\d+$/.test(parts[0])
                                          ? parts.slice(2).join("-")
                                          : cleanRaw;
                                        const ext = (
                                          (typeof attachment === "object" ? attachment.fileExtension : "") ||
                                          fileName.split(".").pop() ||
                                          ""
                                        ).toLowerCase();
                                        const isPdf = ext === "pdf";
                                        const isImg = ["jpg", "jpeg", "png", "webp", "gif"].includes(ext);
                                        const fileSize = typeof attachment === "object" ? attachment.fileSize : undefined;

                                        return (
                                          <div
                                            key={index}
                                            className="d-flex align-items-center justify-content-between rounded-1 shadow-xs attachment-card"
                                            style={{
                                              backgroundColor: "#ffffff",
                                              border: "1px solid #e2e8f0",
                                              padding: "4px 6px",
                                              borderRadius: "5px",
                                              cursor: "pointer",
                                              transition: "all 0.15s ease",
                                              maxWidth: "235px",
                                            }}
                                            onClick={() => handleAttachmentClick(attachment)}
                                            title="Click to open in External Viewer"
                                          >
                                            <div className="d-flex align-items-center overflow-hidden me-1.5" style={{ minWidth: 0 }}>
                                              <div
                                                className="rounded-1 d-flex align-items-center justify-content-center flex-shrink-0 me-1.5"
                                                style={{
                                                  width: "24px",
                                                  height: "24px",
                                                  borderRadius: "4px",
                                                  backgroundColor: isPdf
                                                    ? "#fee2e2"
                                                    : isImg
                                                      ? "#dbeafe"
                                                      : "#f1f5f9",
                                                  color: isPdf
                                                    ? "#dc2626"
                                                    : isImg
                                                      ? "#2563eb"
                                                      : "#475569",
                                                }}
                                              >
                                                <i className={`${isPdf ? "ti ti-file-text" : isImg ? "ti ti-photo" : "ti ti-paperclip"} fs-13`}></i>
                                              </div>
                                              <div className="overflow-hidden" style={{ minWidth: 0 }}>
                                                <span
                                                  className="d-block text-truncate fw-semibold text-decoration-underline"
                                                  style={{
                                                    color: "#0f172a",
                                                    fontSize: "11px",
                                                    lineHeight: "1.2",
                                                    maxWidth: "145px",
                                                  }}
                                                  title={fileName}
                                                >
                                                  {fileName}
                                                </span>
                                                <small
                                                  className="d-block text-truncate"
                                                  style={{
                                                    fontSize: "9px",
                                                    color: "#64748b",
                                                    lineHeight: "1.1",
                                                    marginTop: "1px",
                                                  }}
                                                >
                                                  {fileSize ? `${fileSize} • ` : ""}
                                                  {isPdf ? "PDF" : isImg ? "Image" : ext.toUpperCase() || "File"}
                                                </small>
                                              </div>
                                            </div>

                                            <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-icon rounded-1 p-0 d-flex align-items-center justify-content-center"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleAttachmentClick(attachment);
                                                }}
                                                style={{
                                                  width: "21px",
                                                  height: "21px",
                                                  color: "#334155",
                                                  border: "1px solid #e2e8f0",
                                                  background: "#f8fafc",
                                                  borderRadius: "3px",
                                                }}
                                                title="Open in External Tab"
                                              >
                                                <i className="ti ti-external-link" style={{ fontSize: "11px" }}></i>
                                              </button>
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-icon rounded-1 p-0 d-flex align-items-center justify-content-center"
                                                onClick={(e) => handleDownloadClick(e, attachment)}
                                                style={{
                                                  width: "21px",
                                                  height: "21px",
                                                  color: "#334155",
                                                  border: "1px solid #e2e8f0",
                                                  background: "#f8fafc",
                                                  borderRadius: "3px",
                                                }}
                                                title="Download Attachment"
                                              >
                                                <i className="ti ti-download" style={{ fontSize: "11px" }}></i>
                                              </button>
                                            </div>
                                          </div>
                                        );
                                      }
                                    )}
                                  </div>
                                )}
                              </>
                            );
                          })()}

                          <div className="text-end mt-0.5">
                            <small
                              className="opacity-75"
                              style={{ fontSize: "10px", lineHeight: "1" }}
                            >
                              {formatMessageTime(msg.createdAt)}
                            </small>
                          </div>
                        </div>
                      </div>

                      {isCurrentUserMessage && (
                        <div className="ms-2">
                          <EmployeeAvatar
                            empName={msg.senderName || employeeName}
                            empImage={msg.senderImage || employeeImage}
                            empId={msg.senderEmpId || employeeId}
                            size="sm"
                            showName={false}
                            showEmpId={false}
                            className=""
                          />
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Chat Input */}
            <div className="chat-input-container">
              {/* File Attachments Preview */}
              {selectedFiles.length > 0 && (
                <div className="mb-2 p-2 rounded-2 border bg-light shadow-xs file-attachment-preview">
                  <div className="d-flex align-items-center justify-content-between mb-1.5">
                    <small className="text-muted fw-semibold" style={{ fontSize: "11px" }}>
                      <i className="ti ti-paperclip text-primary me-1"></i>
                      Attached ({selectedFiles.length})
                    </small>
                    <small className="text-muted" style={{ fontSize: "10px" }}>
                      Will be sent combined with your message
                    </small>
                  </div>
                  <div className="d-flex flex-wrap gap-1.5">
                    {selectedFiles.map((file, index) => {
                      const fileSizeKB = (file.size / 1024).toFixed(1);
                      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
                      const displaySize =
                        file.size > 1024 * 1024
                          ? `${fileSizeMB}MB`
                          : `${fileSizeKB}KB`;
                      const ext = file.name.split(".").pop()?.toLowerCase() || "";
                      const isImg = ["jpg", "jpeg", "png", "webp", "gif"].includes(ext);
                      const isPdf = ext === "pdf";

                      return (
                        <div
                          key={index}
                          className="d-flex align-items-center bg-white border rounded-1 px-2 py-1 shadow-xs"
                          style={{ borderRadius: "5px" }}
                        >
                          <div
                            className="rounded-1 d-flex align-items-center justify-content-center me-1.5 flex-shrink-0"
                            style={{
                              width: "20px",
                              height: "20px",
                              backgroundColor: isPdf ? "#fee2e2" : isImg ? "#dbeafe" : "#f1f5f9",
                              color: isPdf ? "#dc2626" : isImg ? "#2563eb" : "#475569",
                              borderRadius: "3px",
                            }}
                          >
                            <i
                              className={isPdf ? "ti ti-file-text" : isImg ? "ti ti-photo" : "ti ti-paperclip"}
                              style={{ fontSize: "11px" }}
                            ></i>
                          </div>
                          <div className="me-2 overflow-hidden" style={{ maxWidth: "155px" }}>
                            <span
                              className="small text-truncate d-block fw-medium text-dark"
                              style={{ fontSize: "11px", lineHeight: 1.2 }}
                              title={file.name}
                            >
                              {file.name}
                            </span>
                            <span
                              className="text-muted d-block"
                              style={{ fontSize: "9px", lineHeight: 1 }}
                            >
                              {displaySize}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="btn btn-xs btn-outline-danger border-0 rounded-circle p-0 d-flex align-items-center justify-content-center"
                            onClick={() => cancelFileUpload(index)}
                            title="Remove attachment"
                            style={{ width: "18px", height: "18px" }}
                          >
                            <i className="ti ti-x" style={{ fontSize: "10px" }}></i>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="d-flex align-items-center">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelection}
                  multiple
                  className="d-none"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                />

                <button
                  type="button"
                  className={`btn ${latestDocRequest && !isCurrentUserApprover
                      ? 'btn-primary'
                      : 'btn-outline-secondary'
                    } rounded-circle me-2 d-flex align-items-center justify-content-center position-relative`}
                  onClick={triggerFileInput}
                  disabled={chatLoading || uploading}
                  style={{ width: "40px", height: "40px" }}
                  title={
                    latestDocRequest && !isCurrentUserApprover
                      ? `Upload ${latestDocRequest.documentType} document`
                      : "Attach files"
                  }
                >
                  {uploading ? (
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                    >
                      <span className="visually-hidden">Uploading...</span>
                    </div>
                  ) : (
                    <>
                      <i className="ti ti-paperclip"></i>
                      {/* Badge to show document type requested */}
                      {latestDocRequest && !isCurrentUserApprover && (
                        <span
                          className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                          style={{ fontSize: '9px' }}
                        >
                          {latestDocRequest.documentType.charAt(0)}
                        </span>
                      )}
                    </>
                  )}
                </button>

                <div className="flex-grow-1 position-relative">
                  <input
                    type="text"
                    className="form-control"
                    placeholder={selectedFiles.length > 0 ? "Type a message for your attachment (required)..." : "Type your message..."}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    disabled={chatLoading || uploading}
                  />
                </div>

                <button
                  className="btn btn-success rounded-circle ms-2 d-flex align-items-center justify-content-center"
                  onClick={handleSendMessage}
                  disabled={chatLoading || uploading || (!newMessage.trim() && selectedFiles.length === 0)}
                  style={{ width: "45px", height: "45px" }}
                >
                  {chatLoading || uploading ? (
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                    >
                      <span className="visually-hidden">
                        {uploading ? "Uploading..." : "Sending..."}
                      </span>
                    </div>
                  ) : (
                    <i className="ti ti-send text-white"></i>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* 🎯 NEW: Document Type Selection Modal */}
      {showDocTypeModal && (
        <>
          {/* Modal Backdrop */}
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1060 }}
            onClick={() => {
              setShowDocTypeModal(false);
              setSelectedDocType(null);
              setPendingMessage("");
            }}
          />

          {/* Modal Dialog */}
          <div
            className="modal fade show d-block"
            style={{ zIndex: 1061 }}
            tabIndex={-1}
            role="dialog"
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content shadow-lg">
                {/* Modal Header */}
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title d-flex align-items-center">
                    <i className="ti ti-file-text me-2"></i>
                    Select Document Type
                  </h5>
                  <button
                    type="button"
                    className="btn-close btn-close-white"
                    onClick={() => {
                      setShowDocTypeModal(false);
                      setSelectedDocType(null);
                      setPendingMessage("");
                    }}
                    aria-label="Close"
                  />
                </div>

                {/* Modal Body */}
                <div className="modal-body p-4">
                  <div className="alert alert-info d-flex align-items-start mb-4">
                    <i className="ti ti-info-circle me-2 mt-1"></i>
                    <div>
                      <strong>Document Request Detected</strong>
                      <p className="mb-0 mt-1 small">
                        Your message mentions documents. Please select the type
                        of document you're requesting from the employee.
                      </p>
                    </div>
                  </div>

                  {/* Your Message Preview */}
                  <div className="mb-4">
                    <label className="form-label text-muted small">
                      Your Message:
                    </label>
                    <div className="border rounded p-3 bg-light">
                      <p className="mb-0">{pendingMessage}</p>
                    </div>
                  </div>

                  {/* Radio Options */}
                  <div className="mb-3">
                    <label className="form-label fw-bold">
                      Document Type Required:
                    </label>

                    {/* Education */}
                    <div className="form-check mb-3 p-3 border rounded hover-shadow">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="docType"
                        id="docTypeEducation"
                        value="Education"
                        checked={selectedDocType === 'Education'}
                        onChange={(e) => setSelectedDocType(e.target.value)}
                      />
                      <label
                        className="form-check-label w-100 cursor-pointer"
                        htmlFor="docTypeEducation"
                      >
                        <div className="d-flex align-items-start">
                          <div className="me-3">
                            <i className="ti ti-school text-primary fs-3"></i>
                          </div>
                          <div className="flex-grow-1">
                            <strong className="d-block text-primary">
                              Education Documents
                            </strong>
                            <small className="text-muted d-block mt-1">
                              Educational certificates, degrees, mark sheets,
                              transcripts, course completion certificates
                            </small>
                          </div>
                        </div>
                      </label>
                    </div>

                    {/* Advertising */}
                    <div className="form-check mb-3 p-3 border rounded hover-shadow">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="docType"
                        id="docTypeAdvertising"
                        value="Advertising"
                        checked={selectedDocType === 'Advertising'}
                        onChange={(e) => setSelectedDocType(e.target.value)}
                      />
                      <label
                        className="form-check-label w-100 cursor-pointer"
                        htmlFor="docTypeAdvertising"
                      >
                        <div className="d-flex align-items-start">
                          <div className="me-3">
                            <i className="ti ti-speakerphone text-warning fs-3"></i>
                          </div>
                          <div className="flex-grow-1">
                            <strong className="d-block text-warning">
                              Advertising Documents
                            </strong>
                            <small className="text-muted d-block mt-1">
                              Advertisement materials, promotional documents,
                              marketing collateral, brochures
                            </small>
                          </div>
                        </div>
                      </label>
                    </div>

                    {/* Others */}
                    <div className="form-check mb-3 p-3 border rounded hover-shadow">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="docType"
                        id="docTypeOthers"
                        value="Others"
                        checked={selectedDocType === 'Others'}
                        onChange={(e) => setSelectedDocType(e.target.value)}
                      />
                      <label
                        className="form-check-label w-100 cursor-pointer"
                        htmlFor="docTypeOthers"
                      >
                        <div className="d-flex align-items-start">
                          <div className="me-3">
                            <i className="ti ti-file text-secondary fs-3"></i>
                          </div>
                          <div className="flex-grow-1">
                            <strong className="d-block text-secondary">
                              Other Documents
                            </strong>
                            <small className="text-muted d-block mt-1">
                              Other supporting documents, miscellaneous files,
                              general attachments
                            </small>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Validation Message */}
                  {!selectedDocType && (
                    <div className="text-danger small">
                      <i className="ti ti-alert-circle me-1"></i>
                      Please select a document type to continue
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="modal-footer bg-light">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowDocTypeModal(false);
                      setSelectedDocType(null);
                      setPendingMessage("");
                    }}
                  >
                    <i className="ti ti-x me-1"></i>
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!selectedDocType}
                    onClick={async () => {
                      if (selectedDocType) {
                        await sendMessageWithDocType(
                          pendingMessage,
                          selectedDocType
                        );
                        setShowDocTypeModal(false);
                        setSelectedDocType(null);
                        setPendingMessage("");
                      }
                    }}
                  >
                    <i className="ti ti-send me-1"></i>
                    Send Message with Document Request
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style>
        {`
          @keyframes slideUp {
            from {
              transform: translateY(100%);
            }
            to {
              transform: translateY(0);
            }
          }
          
          .chat-message {
            margin-bottom: 15px !important;
          }
          
          .chat-message-content {
            word-wrap: break-word;
            word-break: break-word;
            hyphens: auto;
          }
          
          .chat-drawer {
            display: flex;
            flex-direction: column;
            height: 80vh;
          }
          
          .chat-messages-container {
            flex: 1;
            overflow-y: auto;
            padding: 1rem;
            min-height: 0;
          }
          
          .chat-input-container {
            flex-shrink: 0;
            padding: 1rem;
            border-top: 1px solid #dee2e6;
            background-color: #f8f9fa;
          }
          
          .chat-messages-container::after {
            content: '';
            display: block;
            height: 20px;
          }
          
          .file-attachment-preview {
            max-width: 100%;
            overflow-x: auto;
          }
          
          .bg-warning-light {
            background-color: #fff3cd !important;
          }
          
          .file-confirm-btn {
            transition: all 0.2s ease;
          }
          
          .file-confirm-btn:hover {
            transform: scale(1.1);
          }
          
          .attachment-link {
            transition: all 0.2s ease;
          }
          
          .attachment-link:hover {
            opacity: 0.8 !important;
            transform: translateX(2px);
          }

          /* Document Type Modal Styles */
          .hover-shadow {
            transition: all 0.2s ease;
            cursor: pointer;
          }
          
          .hover-shadow:hover {
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            transform: translateY(-1px);
          }
          
          .cursor-pointer {
            cursor: pointer;
          }
          
          .form-check-input:checked ~ .form-check-label {
            color: var(--bs-primary);
          }
          
          .modal-backdrop.show {
            opacity: 0.5;
          }
          
          .modal.show .modal-dialog {
            animation: slideDown 0.3s ease-out;
          }
          
          @keyframes slideDown {
            from {
              transform: translateY(-50px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
        `}
      </style>

      {/* Media / Lightbox Preview Modal */}
      {previewMedia && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 bg-dark bg-opacity-75 d-flex align-items-center justify-content-center"
          style={{ zIndex: 9999, backdropFilter: "blur(4px)" }}
          onClick={() => setPreviewMedia(null)}
        >
          <div
            className="bg-white rounded-3 shadow-lg p-3 position-relative"
            style={{ maxWidth: "90vw", maxHeight: "90vh", minWidth: "320px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex align-items-center justify-content-between mb-2 pb-2 border-bottom">
              <h6 className="mb-0 fw-bold text-dark text-truncate fs-14" style={{ maxWidth: "60%" }}>
                <i className="ti ti-file-text me-1.5 text-primary"></i>
                {previewMedia.title}
              </h6>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-primary py-1 px-2 fs-12"
                  onClick={() => openChatAttachmentInExternalView(previewMedia.url, nocRequestId, nocType, dispatch, attachments)}
                  title="Open in dedicated external tab"
                >
                  <i className="ti ti-external-link me-1"></i>External View
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary py-1 px-2 fs-12"
                  onClick={() => downloadChatAttachment(previewMedia.url, nocRequestId, nocType, dispatch, attachments)}
                  title="Download file"
                >
                  <i className="ti ti-download me-1"></i>Download
                </button>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setPreviewMedia(null)}
                ></button>
              </div>
            </div>
            <div className="text-center overflow-auto p-2" style={{ maxHeight: "calc(90vh - 100px)" }}>
              {previewMedia.isImage ? (
                <img
                  src={previewMedia.url}
                  alt={previewMedia.title}
                  className="img-fluid rounded"
                  style={{ maxHeight: "75vh", objectFit: "contain" }}
                />
              ) : (
                <iframe
                  src={`${previewMedia.url}#toolbar=1`}
                  title={previewMedia.title}
                  style={{ width: "80vw", height: "70vh", border: "none" }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NOCChatComponent;
